import React from "react";
import { LegalPage, H2, H3, P, UL, Address, Note } from "@/components/LegalPage";

export default function Datenschutz() {
  return (
    <LegalPage title="Datenschutzerklärung" kicker="Privacy Policy">
      <Note>
        Diese Datenschutzerklärung gilt für die Webseite{" "}
        <span className="font-mono">help4emergency.de</span> sowie die zugehörige Trainings-App
        unter <span className="font-mono">app.help4emergency.de</span>.
      </Note>

      {/* 1 ---------------------------------------------------------------- */}
      <H2 id="verantwortlicher">1. Verantwortliche Stelle</H2>
      <P>
        Verantwortlich im Sinne der Datenschutz-Grundverordnung (DSGVO) und anderer nationaler
        Datenschutzgesetze sowie sonstiger datenschutzrechtlicher Bestimmungen ist:
      </P>
      <Address>
        <strong>help4help GmbH</strong>
        <br />
        Oskar-von-Miller-Straße 22
        <br />
        82291 Mammendorf
        <br />
        Deutschland
        <br />
        <br />
        Telefon: +49 151 / 22 94 23 02
        <br />
        E-Mail:{" "}
        <a
          href="mailto:servus@help4help.de"
          className="text-[#A7B584] hover:text-[#8C9A69] underline underline-offset-4"
        >
          servus@help4help.de
        </a>
      </Address>
      <P>
        Verantwortliche Stelle ist die natürliche oder juristische Person, die allein oder
        gemeinsam mit anderen über die Zwecke und Mittel der Verarbeitung von personenbezogenen
        Daten (z. B. Namen, E-Mail-Adressen o. Ä.) entscheidet.
      </P>

      {/* 2 ---------------------------------------------------------------- */}
      <H2 id="ueberblick">2. Überblick — welche Daten verarbeiten wir?</H2>
      <P>
        Wir verarbeiten personenbezogene Daten ausschließlich, um die Webseite und die
        Trainings-App bereitzustellen sowie zur Beantwortung von Support-Anfragen. Eine
        Weitergabe oder ein Verkauf an Dritte zu Werbezwecken findet nicht statt.
      </P>

      {/* 3 ---------------------------------------------------------------- */}
      <H2 id="server-logs">3. Server-Logfiles beim Aufruf der Webseite</H2>
      <P>
        Beim Aufruf von <span className="font-mono">help4emergency.de</span> werden technisch
        erforderliche Daten in Logfiles erhoben (IP-Adresse, Datum/Uhrzeit, aufgerufene URL,
        User-Agent, Referrer). Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO (berechtigtes
        Interesse an einem stabilen, sicheren Betrieb). Die Daten werden nach maximal 14 Tagen
        gelöscht oder anonymisiert.
      </P>

      {/* 4 ---------------------------------------------------------------- */}
      <H2 id="cookies">4. Cookies & lokale Speicherung</H2>
      <P>
        Wir setzen ausschließlich technisch notwendige Cookies (keine Marketing- oder
        Tracking-Cookies) zur Authentifizierung. Rechtsgrundlage ist § 25 Abs. 2 Nr. 2 TTDSG in
        Verbindung mit Art. 6 Abs. 1 lit. b und f DSGVO.
      </P>
      <UL>
        <li>
          <strong>access_token</strong> — JWT-Sitzungscookie nach Login (httpOnly, Secure,
          SameSite=None, Lebensdauer 1 Tag).
        </li>
        <li>
          <strong>refresh_token</strong> — JWT-Erneuerungscookie (httpOnly, Secure,
          SameSite=None, Lebensdauer 30 Tage).
        </li>
        <li>
          <strong>session_token</strong> — wird gesetzt, wenn Sie sich über Google anmelden
          (Lebensdauer 7 Tage).
        </li>
      </UL>
      <P>
        Sie können diese Cookies jederzeit über die „Abmelden"-Funktion bzw. die
        Browser-Einstellungen löschen. Ohne diese Cookies ist eine Anmeldung technisch nicht
        möglich. Ihre Cookie-Einwilligung selbst speichern wir lokal in Ihrem Browser
        (<span className="font-mono">localStorage</span>, Schlüssel{" "}
        <span className="font-mono">h4e_cookie_consent_v1</span>) — keine Übertragung an uns.
      </P>

      {/* 5 ---------------------------------------------------------------- */}
      <H2 id="registrierung">5. Registrierung & Anmeldung</H2>
      <P>
        Bei der Registrierung als Early Tester verarbeiten wir folgende Daten:
      </P>
      <UL>
        <li>Name (optional, freiwillig)</li>
        <li>E-Mail-Adresse</li>
        <li>Passwort (wird ausschließlich als Hash mit dem Verfahren <em>bcrypt</em> gespeichert; das Klartextpasswort verlässt nie unseren Server-Speicher)</li>
        <li>Anmeldeart (lokal oder Google)</li>
        <li>Erstellungszeitpunkt</li>
      </UL>
      <P>
        Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung / vorvertragliche
        Maßnahmen). Die Daten bleiben gespeichert, solange Ihr Account besteht. Sie können
        jederzeit per E-Mail an{" "}
        <a
          href="mailto:servus@help4help.de"
          className="text-[#A7B584] hover:text-[#8C9A69] underline underline-offset-4"
        >
          servus@help4help.de
        </a>{" "}
        die Löschung verlangen.
      </P>

      <H3 id="google-auth">5.1 Anmeldung über Google (Emergent-managed)</H3>
      <P>
        Optional bieten wir die Anmeldung über Google an. Diese läuft über den
        OAuth-Vermittlungsdienst <strong>Emergent Auth</strong>{" "}
        (Emergent Labs Inc., USA). Beim Klick auf „Mit Google anmelden" werden Sie auf{" "}
        <span className="font-mono">auth.emergentagent.com</span> weitergeleitet, dort
        authentifizieren Sie sich bei Google und werden anschließend mit einer kurzlebigen
        Session-ID zu uns zurückgeleitet. Unser Backend tauscht diese Session-ID auf
        <span className="font-mono"> demobackend.emergentagent.com</span> gegen Ihre Profildaten
        (E-Mail, Name, Profilbild-URL) ein.
      </P>
      <P>
        Es findet eine Datenübermittlung in die USA statt. Rechtsgrundlage ist Art. 6 Abs. 1
        lit. a DSGVO (Einwilligung durch aktive Nutzung der Schaltfläche) sowie Art. 49 Abs. 1
        lit. a DSGVO. Wir empfehlen, alternativ die E-Mail-/Passwort-Anmeldung zu nutzen, wenn
        Sie keine Datenübermittlung in die USA wünschen.
      </P>

      {/* 6 ---------------------------------------------------------------- */}
      <H2 id="support">6. Support-Anfragen</H2>
      <P>
        Wenn Sie unser Support-Formular nutzen, verarbeiten wir Name, E-Mail-Adresse,
        Kategorie, Betreff, Beschreibung und ggf. die von Ihnen angegebene Screenshot-URL.
        Rechtsgrundlage ist Art. 6 Abs. 1 lit. b und f DSGVO. Tickets werden für maximal 24
        Monate aufbewahrt, danach gelöscht oder anonymisiert. Eine Anbindung an Zendesk
        (Zendesk Inc., USA) ist in Vorbereitung; vor Aktivierung dieser Anbindung wird diese
        Erklärung entsprechend angepasst.
      </P>

      {/* 7 ---------------------------------------------------------------- */}
      <H2 id="schriften-bilder">7. Schriftarten & Bildmaterial (Externe Medien)</H2>
      <P>
        Wir verwenden die freie Schriftart <strong>DM Sans</strong>, die über das CDN von
        Google Fonts (Google Ireland Limited / Google LLC) geladen wird. Beim Laden der
        Schriftart kann Ihre IP-Adresse an Google übertragen werden. Bildmaterial auf der
        Webseite stammt teilweise von <strong>Unsplash</strong> (Unsplash Inc., USA) und{" "}
        <strong>Pexels</strong> (Pexels GmbH, Berlin) und wird direkt von deren CDN
        ausgeliefert. Auch hier kann Ihre IP-Adresse an die jeweiligen Anbieter übertragen
        werden. Es findet keine Setzung von Cookies durch diese Anbieter statt.
      </P>
      <P>
        Diese externen Medien laden wir nur mit Ihrer Einwilligung in der Kategorie „Externe
        Medien" unseres Cookie-Banners. Rechtsgrundlage ist Art. 6 Abs. 1 lit. a DSGVO. Sie
        können Ihre Einwilligung jederzeit über das Cookie-Symbol unten links auf der Webseite
        widerrufen. Weitere Informationen:{" "}
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#A7B584] hover:text-[#8C9A69] underline underline-offset-4"
        >
          policies.google.com/privacy
        </a>
        .
      </P>

      {/* 8 ---------------------------------------------------------------- */}
      <H2 id="hosting">8. Hosting</H2>
      <P>
        Webseite und API werden auf der Plattform <strong>Emergent</strong> (Emergent Labs
        Inc.) gehostet. Die Datenverarbeitung erfolgt im Rahmen eines
        Auftragsverarbeitungsvertrages. Datenbank: <strong>MongoDB</strong>. Übertragung
        ausschließlich verschlüsselt per TLS / HTTPS.
      </P>

      {/* 9 ---------------------------------------------------------------- */}
      <H2 id="app">9. Verarbeitung in der Trainings-App (app.help4emergency.de)</H2>
      <P>
        Die Trainings-App ist eine eigenständige Anwendung der help4help GmbH. Sie nutzen sie
        i. d. R., wenn Ihre Kursleitung einen Kurs angelegt und Ihnen einen QR-Code zur
        Verfügung gestellt hat. Folgende Daten werden in der App verarbeitet:
      </P>

      <H3 id="app-daten">9.1 Daten von Teilnehmenden</H3>
      <UL>
        <li>Name (selbstgewählt, kann auch ein Pseudonym sein)</li>
        <li>Einwilligung zur Verarbeitung (DSGVO-konform protokolliert)</li>
        <li>Auswahl der Stimme der KI-Leitstelle (Bella oder Daniel)</li>
        <li>Transkript des simulierten Notruf-Gesprächs (Texteingabe und/oder erkannte Sprache)</li>
        <li>Score (0–100) sowie automatisch generiertes Feedback</li>
        <li>Optional: E-Mail-Adresse, falls Sie einen Report per E-Mail wünschen</li>
      </UL>
      <P>
        Rechtsgrundlage ist Art. 6 Abs. 1 lit. a DSGVO (Einwilligung) sowie Art. 6 Abs. 1
        lit. b DSGVO. Trainingsdaten werden <strong>spätestens 90 Tage nach Erstellung
        automatisch gelöscht</strong>.
      </P>

      <H3 id="app-kursleitung">9.2 Daten von Kursleitenden</H3>
      <UL>
        <li>Name der Kursleitung</li>
        <li>Kurstitel, Datum, Gültigkeitsdauer (Minuten), Anmerkungen</li>
        <li>E-Mail-Adresse für den Versand des Handouts mit QR-Code</li>
        <li>Statistische Auswertungen des Kurses (anonymisiert / pseudonymisiert)</li>
      </UL>

      <H3 id="app-mikrofon">9.3 Mikrofon-Zugriff</H3>
      <P>
        Für die realistische Sprachsimulation bittet die App um Mikrofon-Zugriff. Die
        Spracherkennung läuft <strong>lokal in Ihrem Browser</strong> über die Web Speech API
        — Audio wird nicht an einen Drittanbieter zur Erkennung übertragen. Wer keinen
        Mikrofon-Zugriff erlauben möchte, kann den Text-Fallback nutzen.
      </P>

      <H3 id="app-tools">9.4 Eingesetzte Verarbeitungs-Tools</H3>
      <UL>
        <li>
          <strong>Anthropic Claude Sonnet 4.5</strong> — KI-Sprachmodell für die Gesprächs-
          und Bewertungslogik. Anbieter: Anthropic, PBC, USA. Übermittelt werden ausschließlich
          die Texte des Notruf-Gesprächs (kein Audio, keine direkten Identifikatoren) zur
          Erzeugung der Antwort und des Scores. Anthropic verarbeitet API-Inhalte laut eigener
          Zusicherung nicht zum Training der Modelle. Datenübermittlung in die USA.
        </li>
        <li>
          <strong>ElevenLabs</strong> — Sprachsynthese (Text-to-Speech) für die Stimmen Bella
          und Daniel. Anbieter: ElevenLabs, Inc., USA. Übermittelt werden ausschließlich die
          Antwort-Texte der KI-Leitstelle zur Sprachausgabe. Datenübermittlung in die USA.
        </li>
        <li>
          <strong>Web Speech API</strong> — browser-eigene Spracherkennung. Audio wird lokal in
          Ihrem Browser verarbeitet; an uns gelangt nur das resultierende Transkript.
        </li>
        <li>
          <strong>MongoDB</strong> — Speicherung von Kurs-, Anrufs- und Score-Daten;
          ausschließlich verschlüsselte Übertragung.
        </li>
      </UL>
      <P>
        Datenübermittlungen an Anthropic und ElevenLabs erfolgen auf Grundlage der
        EU-Standardvertragsklauseln und ergänzender Maßnahmen (verschlüsselter Transport,
        minimaler Inhalt, keine direkten Identifikatoren). Sofern eine ausdrückliche
        Einwilligung erforderlich ist, holen wir diese vorab in der App ein (Art. 6 Abs. 1
        lit. a, Art. 49 Abs. 1 lit. a DSGVO).
      </P>

      <H3 id="app-loeschung">9.5 Speicherdauer & Auto-Löschung</H3>
      <P>
        Trainingsgespräche, Transkripte und Scores werden spätestens nach 90 Tagen
        automatisch gelöscht. Sie können jederzeit eine sofortige Löschung verlangen — siehe
        Abschnitt „Ihre Rechte".
      </P>

      {/* 10 --------------------------------------------------------------- */}
      <H2 id="rechte">10. Ihre Rechte als Betroffene:r</H2>
      <P>Sie haben jederzeit das Recht auf:</P>
      <UL>
        <li>Auskunft über die zu Ihrer Person gespeicherten Daten (Art. 15 DSGVO)</li>
        <li>Berichtigung unrichtiger Daten (Art. 16 DSGVO)</li>
        <li>Löschung Ihrer Daten (Art. 17 DSGVO)</li>
        <li>Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
        <li>Datenübertragbarkeit (Art. 20 DSGVO)</li>
        <li>Widerspruch gegen die Verarbeitung (Art. 21 DSGVO)</li>
        <li>Widerruf einer erteilten Einwilligung mit Wirkung für die Zukunft (Art. 7 Abs. 3 DSGVO)</li>
      </UL>
      <P>
        Zur Wahrnehmung Ihrer Rechte genügt eine formlose Nachricht an{" "}
        <a
          href="mailto:servus@help4help.de"
          className="text-[#A7B584] hover:text-[#8C9A69] underline underline-offset-4"
        >
          servus@help4help.de
        </a>
        .
      </P>

      <H3 id="aufsichtsbehoerde">10.1 Beschwerderecht</H3>
      <P>
        Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde zu beschweren. Für
        unseren Sitz zuständig ist:
      </P>
      <Address>
        Bayerisches Landesamt für Datenschutzaufsicht (BayLDA)
        <br />
        Promenade 18
        <br />
        91522 Ansbach
      </Address>

      {/* 11 --------------------------------------------------------------- */}
      <H2 id="ssl">11. Verschlüsselung</H2>
      <P>
        Diese Seite verwendet aus Sicherheitsgründen eine SSL-/TLS-Verschlüsselung. Eine
        verschlüsselte Verbindung erkennen Sie am „https://" in der Adresszeile Ihres Browsers
        sowie am Schloss-Symbol.
      </P>

      {/* 12 --------------------------------------------------------------- */}
      <H2 id="alpha">12. Hinweis zum Alpha-Stand</H2>
      <P>
        Help4Emergency befindet sich aktuell im <strong>Alpha-Stand</strong>. Funktionen,
        Datenflüsse und eingesetzte Verarbeiter können sich noch ändern. Wesentliche
        Änderungen, die Auswirkungen auf den Datenschutz haben, kommunizieren wir per E-Mail
        und auf dieser Seite. Stand dieser Erklärung: Dezember 2025.
      </P>
    </LegalPage>
  );
}
